/**
 * FriendsPageContext - Page-specific context for friends page
 * Provides all friends page data, actions, and state
 */

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'
import { usePathname } from 'expo-router'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { USER_CONFIG } from '../../../constants'
import { useFriendsMatchesData } from '../hooks/useFriendsMatchesData'
import { useFriendsActions } from '../hooks/useFriendsActions'
import { useFriendsInvitations } from '../hooks/useFriendsInvitations'
import { getUserDisplayName } from '../helpers/friendsUser.helper'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics'
import { useFriendsData } from '../../../contexts/FriendsDataContext'
import { useGroupsQuery } from '../hooks/useGroupsQuery'
import { STALE_TIME_GROUPS_MS } from '../constants/reactQuery.constants'
import { useQueryClient } from '@tanstack/react-query'
import type {
  Friend,
  FriendMatchSummary,
  BetSelection,
} from '../types/friends.types'
import type { Group } from '../types/groups.types'
import type { SnackbarVariant } from '../../shared/stores/snackbarStore'
import { useSnackbarStore } from '../../shared/stores/snackbarStore'
import { useSnackbar } from '../../shared/hooks/useSnackbar'

interface FriendsPageContextValue {
  // Friends data (from global context)
  friends: Friend[]
  refreshFriends: () => Promise<void>

  // Groups data
  groups: Group[]
  loadingGroups: boolean
  refreshGroups: () => Promise<void>

  // Page-specific data (from useFriendsMatchesData)
  loading: boolean
  userSelections: Record<string, BetSelection>
  setUserSelections: React.Dispatch<React.SetStateAction<Record<string, BetSelection>>>
  originalSelections: Record<string, BetSelection>
  setOriginalSelections: React.Dispatch<React.SetStateAction<Record<string, BetSelection>>>
  expandedFriendId: string | null
  setExpandedFriendId: React.Dispatch<React.SetStateAction<string | null>>
  updatingFriendId: string | null
  setUpdatingFriendId: React.Dispatch<React.SetStateAction<string | null>>
  getFriendMatches: (friendId: string) => FriendMatchSummary[]
  friendHasChanges: (friendId: string) => boolean
  deduplicateFriendsByEmail: (friends: Friend[]) => Friend[]

  // Friend actions (from useFriendActions)
  handleRemoveFriend: (friendId: string, friend?: Friend) => void
  handleUpdateFriendMatches: (friendId: string) => Promise<void>
  handlePickSelect: (matchId: string, selection: Exclude<BetSelection, null>) => void
  toggleFriendCard: (friendId: string) => void
  handleAcceptInvitation: (token: string, invitationId: string) => Promise<void>
  handleDeclineInvitation: (invitationId: string) => Promise<void>
  actionLoadingId: string | null

  // Invitation state and actions (from useInvitations)
  showInviteModal: boolean
  setShowInviteModal: React.Dispatch<React.SetStateAction<boolean>>
  inviteTab: 'manual' | 'link'
  setInviteTab: React.Dispatch<React.SetStateAction<'manual' | 'link'>>
  selectedContacts: Set<string>
  setSelectedContacts: React.Dispatch<React.SetStateAction<Set<string>>>
  inviteMessage: string
  setInviteMessage: React.Dispatch<React.SetStateAction<string>>
  sendingInvites: boolean
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  manualEmail: string
  setManualEmail: React.Dispatch<React.SetStateAction<string>>
  manualEmails: Set<string>
  setManualEmails: React.Dispatch<React.SetStateAction<Set<string>>>
  handleCloseModal: () => void
  toggleContactSelection: (email: string) => void
  addManualEmail: () => void
  removeManualEmail: (email: string) => void
  handleSendInvitations: () => Promise<void>

  // Snackbar
  setSnackbar: React.Dispatch<React.SetStateAction<{ message: string; variant: SnackbarVariant } | null>>
}

/** Exported for Storybook mock providers – prefer useFriendsPage() in production code */
export const FriendsPageContext = createContext<FriendsPageContextValue | undefined>(undefined)

interface FriendsPageProviderProps {
  children: React.ReactNode
}

export const FriendsPageProvider: React.FC<FriendsPageProviderProps> = ({ children }) => {
  const { userId: authUserId, isSignedIn, isLoaded, user: authUser } = useSupabaseAuth()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const pathname = usePathname()

  const currentUserId = isSignedIn ? (authUserId ?? '') : USER_CONFIG.defaultUserId
  const isFriendsRoute = pathname?.includes('/friends') ?? false

  const { friends, refreshFriends } = useFriendsData()

  const {
    data: groups = [],
    isLoading: loadingGroups,
    refetch: refetchGroups,
  } = useGroupsQuery(currentUserId, isSignedIn === true && isLoaded === true && !!authUserId)

  useEffect(() => {
    if (isSignedIn === true && isLoaded === true && !!authUserId && currentUserId) {
      queryClient.prefetchQuery({
        queryKey: ['groups', currentUserId],
        queryFn: async () => {
          const { fetchGroups } = await import('../helpers/groupsApi')
          return fetchGroups(currentUserId, apiClient)
        },
        staleTime: STALE_TIME_GROUPS_MS,
      })
    }
  }, [isSignedIn, isLoaded, currentUserId, queryClient, apiClient])

  const refreshGroups = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['groups', currentUserId] })
    await refetchGroups()
  }, [queryClient, currentUserId, refetchGroups])

  const showSnackbar = useSnackbar().showSnackbar
  const setSnackbar = useCallback<
    React.Dispatch<React.SetStateAction<{ message: string; variant: SnackbarVariant } | null>>
  >(
    (action) => {
      const payload = typeof action === 'function' ? action(null) : action
      if (payload) showSnackbar(payload.message, payload.variant)
      else useSnackbarStore.getState().clearSnackbar()
    },
    [showSnackbar]
  )

  const {
    loading,
    userSelections,
    setUserSelections,
    originalSelections,
    setOriginalSelections,
    expandedFriendId,
    setExpandedFriendId,
    updatingFriendId,
    setUpdatingFriendId,
    getFriendMatches,
    friendHasChanges,
    deduplicateFriendsByEmail,
  } = useFriendsMatchesData({
    currentUserId,
    isSignedIn: isFriendsRoute ? isSignedIn : false,
    isLoaded: isFriendsRoute ? isLoaded : false,
    friends,
  })

  const {
    handleRemoveFriend,
    handleUpdateFriendMatches,
    handlePickSelect,
    toggleFriendCard,
    handleAcceptInvitation,
    handleDeclineInvitation,
    actionLoadingId,
  } = useFriendsActions({
    currentUserId,
    getFriendMatches,
    expandedFriendId,
    setExpandedFriendId,
    userSelections,
    setUserSelections,
    originalSelections,
    setOriginalSelections,
    updatingFriendId,
    setUpdatingFriendId,
    refreshFriends,
    setSnackbar,
  })

  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteTab, setInviteTab] = useState<'manual' | 'link'>('manual')
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set())
  const [inviteMessage, setInviteMessage] = useState('')
  const [sendingInvites, setSendingInvites] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [manualEmails, setManualEmails] = useState<Set<string>>(new Set())

  const { track } = useAnalytics()

  const {
    handleCloseModal,
    toggleContactSelection,
    addManualEmail,
    removeManualEmail,
    handleSendInvitations,
  } = useFriendsInvitations({
    setShowInviteModal,
    setSelectedContacts,
    setInviteMessage,
    setSearchQuery,
    setManualEmail,
    setManualEmails,
    setInviteTab,
    setSendingInvites,
    setSnackbar,
    refreshFriends,
    selectedContacts,
    manualEmail,
    manualEmails,
    inviteMessage,
    friends,
    currentUserId,
    getUserDisplayName: () => getUserDisplayName(authUser),
    deduplicateFriendsByEmail,
    apiClient,
    inviteTab,
    onInvitesSent: (method, count) =>
      track(ANALYTICS_EVENTS.INVITE_SENT, { method, count }),
  })

  const value = useMemo(
    () => ({
      friends,
      refreshFriends,
      groups,
      loadingGroups,
      refreshGroups,
      loading,
      userSelections,
      setUserSelections,
      originalSelections,
      setOriginalSelections,
      expandedFriendId,
      setExpandedFriendId,
      updatingFriendId,
      setUpdatingFriendId,
      getFriendMatches,
      friendHasChanges,
      deduplicateFriendsByEmail,
      handleRemoveFriend,
      handleUpdateFriendMatches,
      handlePickSelect,
      toggleFriendCard,
      handleAcceptInvitation,
      handleDeclineInvitation,
      actionLoadingId,
      showInviteModal,
      setShowInviteModal,
      inviteTab,
      setInviteTab,
      selectedContacts,
      setSelectedContacts,
      inviteMessage,
      setInviteMessage,
      sendingInvites,
      searchQuery,
      setSearchQuery,
      manualEmail,
      setManualEmail,
      manualEmails,
      setManualEmails,
      handleCloseModal,
      toggleContactSelection,
      addManualEmail,
      removeManualEmail,
      handleSendInvitations,
      setSnackbar,
    }),
    [
      friends,
      refreshFriends,
      groups,
      loadingGroups,
      refreshGroups,
      loading,
      userSelections,
      setUserSelections,
      originalSelections,
      setOriginalSelections,
      expandedFriendId,
      setExpandedFriendId,
      updatingFriendId,
      setUpdatingFriendId,
      getFriendMatches,
      friendHasChanges,
      deduplicateFriendsByEmail,
      handleRemoveFriend,
      handleUpdateFriendMatches,
      handlePickSelect,
      toggleFriendCard,
      handleAcceptInvitation,
      handleDeclineInvitation,
      actionLoadingId,
      showInviteModal,
      setShowInviteModal,
      inviteTab,
      setInviteTab,
      selectedContacts,
      setSelectedContacts,
      inviteMessage,
      setInviteMessage,
      sendingInvites,
      searchQuery,
      setSearchQuery,
      manualEmail,
      setManualEmail,
      manualEmails,
      setManualEmails,
      handleCloseModal,
      toggleContactSelection,
      addManualEmail,
      removeManualEmail,
      handleSendInvitations,
      setSnackbar,
    ]
  )

  return (
    <FriendsPageContext.Provider value={value}>
      {children}
    </FriendsPageContext.Provider>
  )
}

export const useFriendsPage = (): FriendsPageContextValue => {
  const context = useContext(FriendsPageContext)
  if (context === undefined) {
    throw new Error('useFriendsPage must be used within a FriendsPageProvider')
  }
  return context
}
